import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ArticleCard, coverUrl, KindBadge, type ArticleCardData } from '../components/ArticleCard';
import { RichText } from '../lib/richtext';
import { useApi } from '../lib/useApi';
import { usePortal } from '../lib/portal';
import { shortDate } from '../lib/format';
import { Alert, ErrorBlock, LoadingBlock } from '../components/ui';
import '../styles/public.css';
import '../styles/insights.css';
import '../styles/blog.css';

/**
 * One article.
 *
 * The body is rendered through RichText, which produces React elements and
 * never HTML — see lib/richtext.tsx. Nothing on this page uses
 * dangerouslySetInnerHTML, which is what makes a pasted script tag harmless
 * rather than something a sanitiser has to catch.
 */

interface Detail extends ArticleCardData {
  body: string;
  seo_title: string | null;
  seo_description: string | null;
  sponsor_url: string | null;
}
interface Response { article: Detail; related: ArticleCardData[] }

/** Where a reader can take an article next, chosen by what they were reading. */
const CTAS: Record<string, { label: string; to: string; blurb: string }> = {
  'landlord-tips':        { label: 'Start managing your property', to: '/register', blurb: 'Rent, tenants and receipts in one place.' },
  'property-management':  { label: 'Start managing your property', to: '/register', blurb: 'Rent, tenants and receipts in one place.' },
  'rent-and-finance':     { label: 'See how rent tracking works',  to: '/solutions', blurb: 'Charges raised monthly, balances that follow.' },
  'tenant-tips':          { label: 'Find your next home',          to: '/homes',    blurb: 'Homes to rent across Kenya.' },
  'real-estate':          { label: 'Find your next home',          to: '/homes',    blurb: 'Homes to rent across Kenya.' },
  'property-maintenance': { label: 'Request a demo',               to: '/demo',     blurb: 'See maintenance tracking in practice.' },
  'customer-stories':     { label: 'Share your story',             to: '/blog/share-your-story', blurb: 'Tell us what you have learned.' },
};
const DEFAULT_CTA = { label: 'Request a demo', to: '/demo', blurb: 'See NyumbaSuite on your own portfolio.' };

function Share({ title, slug }: { title: string; slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window === 'undefined' ? '' : `${window.location.origin}/blog/${slug}`;
  const enc = encodeURIComponent;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; say so rather than appearing to work.
      setCopied(false);
    }
  };

  return (
    <div className="ins-share">
      <span className="ins-share-label">Share</span>
      {/* Plain links, no third-party scripts: a share button that loads a
          tracker on every article reader is a privacy cost for a nicety. */}
      <a className="ins-share-btn" target="_blank" rel="noopener noreferrer"
        href={`https://wa.me/?text=${enc(`${title} ${url}`)}`}>WhatsApp</a>
      <a className="ins-share-btn" target="_blank" rel="noopener noreferrer"
        href={`https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`}>X</a>
      <a className="ins-share-btn" target="_blank" rel="noopener noreferrer"
        href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`}>Facebook</a>
      <a className="ins-share-btn" target="_blank" rel="noopener noreferrer"
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`}>LinkedIn</a>
      <button type="button" className="ins-share-btn" onClick={copy}>
        {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
}

/** A thin bar under the header that fills as the article is read. */
function ReadingBar() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const on = () => {
      const el = document.querySelector('.ins-article-body');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight * 0.5;
      setW(total <= 0 ? 0 : Math.min(100, Math.max(0, (-r.top / total) * 100)));
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, []);
  return <div className="ins-progress" aria-hidden><i style={{ width: `${w}%` }} /></div>;
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { siteHome } = usePortal();
  const view = useApi<Response>(slug ? `/api/v1/content/${slug}` : null);
  const a = view.data?.article;

  /**
   * Sets the tab title and description for this article.
   *
   * The server injects the same values into the HTML it serves for this route,
   * which is what a crawler reads; this is for the reader's tab and for
   * in-app navigation, where no fresh document is fetched.
   */
  useEffect(() => {
    if (!a) return;
    const previousTitle = document.title;
    document.title = `${a.seo_title || a.title} · NyumbaSuite`;
    const meta = document.querySelector('meta[name="description"]');
    const previousDesc = meta?.getAttribute('content') ?? null;
    if (meta) meta.setAttribute('content', a.seo_description || a.summary || '');
    return () => {
      document.title = previousTitle;
      if (meta && previousDesc !== null) meta.setAttribute('content', previousDesc);
    };
  }, [a]);

  if (view.loading) return <><PublicHeader /><div className="page"><LoadingBlock /></div><SiteFooter /></>;
  if (view.error || !a) {
    return (
      <>
        <PublicHeader />
        <div className="page ins-gone">
          {view.error?.status === 404 ? (
            <>
              <h1>That article is not available</h1>
              <p className="page-sub">It may have been unpublished or moved.</p>
              <Link className="btn btn-primary" to="/blog">Back to Blog</Link>
            </>
          ) : view.error ? <ErrorBlock error={view.error} onRetry={view.reload} /> : null}
        </div>
        <SiteFooter />
      </>
    );
  }

  const cta = CTAS[a.category_slug ?? ''] ?? DEFAULT_CTA;

  return (
    <>
      <PublicHeader />

      <ReadingBar />
      <article className="ins-article">
        <nav className="ins-crumbs" aria-label="Breadcrumb">
          <Link to={siteHome}>Home</Link>
          <span aria-hidden> › </span>
          <Link to="/blog">Blog</Link>
          {a.category_slug && (
            <>
              <span aria-hidden> › </span>
              <Link to={`/blog?category=${a.category_slug}`}>{a.category_name}</Link>
            </>
          )}
        </nav>

        <header className="ins-article-head">
          <p className="ins-card-meta">
            <KindBadge kind={a.kind} sponsor={a.sponsor_name} />
          </p>
          <h1>{a.title}</h1>
          {a.summary && <p className="ins-lede">{a.summary}</p>}
          <p className="ins-byline">
            <span className="ins-av lg" aria-hidden>{a.author_name.trim().charAt(0).toUpperCase()}</span>
            <span className="ins-byline-name">{a.author_name}</span>
            {a.author_title && <span className="muted">{a.author_title}</span>}
            {a.published_at && <span className="muted">{shortDate(a.published_at)}</span>}
            <span className="muted">{a.reading_minutes} min read</span>
          </p>
        </header>

        {/* Stated above the article, not buried at the end, because a reader
            deciding whether to trust it should know before they read it. */}
        {a.kind === 'sponsored' && (
          <div className="ins-sponsored-note">
            <Alert tone="warning">
              <strong>Sponsored.</strong> This article was paid for by {a.sponsor_name}.
              It is published as a service to readers and is not editorial coverage.
              {a.sponsor_url && (
                <> <a href={a.sponsor_url} target="_blank" rel="noopener noreferrer nofollow sponsored">
                  Visit {a.sponsor_name}
                </a>.</>
              )}
            </Alert>
          </div>
        )}
        {a.kind === 'story' && (
          <div className="ins-sponsored-note">
            <Alert tone="info">
              A reader&rsquo;s story, shared with permission and edited for length.
            </Alert>
          </div>
        )}

        {a.cover_image_id && (
          <img className="ins-article-cover" src={coverUrl(a.cover_image_id)} alt="" />
        )}

        <div className="ins-article-body">
          <RichText body={a.body} />
        </div>

        {a.tags.length > 0 && (
          <p className="ins-tags">
            {a.tags.map(t => (
              <Link key={t} className="ins-tag" to={`/blog?tag=${encodeURIComponent(t)}`}>#{t}</Link>
            ))}
          </p>
        )}

        <Share title={a.title} slug={a.slug} />

        <aside className="ins-nextstep">
          <h2>{cta.label}</h2>
          <p>{cta.blurb}</p>
          <Link className="btn btn-primary" to={cta.to}>{cta.label}</Link>
        </aside>
      </article>

      {view.data!.related.length > 0 && (
        <section className="ins-related">
          <div className="ins-section-head"><h2>Related reading</h2></div>
          <div className="ins-grid">
            {view.data!.related.map(r => <ArticleCard key={r.id} article={r} />)}
          </div>
        </section>
      )}

      <SiteFooter />
    </>
  );
}
